import { Component, ErrorInfo, PropsWithChildren, ReactNode } from 'react';

type ErrorBoundaryProps = PropsWithChildren<{
  readonly fallback: ReactNode;
  readonly name?: string;
  readonly onError?: (error: unknown, info: ErrorInfo, name?: string) => void;
}>;

type ErrorBoundaryState = {
  readonly hasError: boolean;
  readonly error: unknown;
};

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: unknown) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    const stackTrace = info.componentStack;
    const errorMessage = error instanceof Error ? error.message : String(error);

    console.error({
      name: this.props.name,
      errorMessage,
      stackTrace,
    });

    this.props.onError?.(error, info, this.props.name);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }

    return this.props.children;
  }
}
